import { MessageBanner, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import { emailRegex, mobileRegex } from '@by-miles/regex'
import React, { useContext, useEffect, useState } from 'react'

import { genericErrorCopy } from '../../../../constants'
import { useEmailCheckLazyQuery } from '../../../../generated/graphql'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { MarketingProps, MarketingToggles } from '../../../MarketingToggles'
import { ExistingMemberModal } from '../../../Overlays/ExistingMemberModal'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { DriverContactDetailsStyle } from './DriverContactDetailsStyle'
import { GenericContactQuestion } from './GenericContactQuestion'
import { emailProps, mobileProps } from './GenericContactQuestion/utils/props'
const { h4: H4, h5: H5, paragraphBody: ParagraphBody } = typographyRem

export const DriverContactDetails = () => {
  const quote = useQuote()
  const {
    state: { showModal, activeModal, selectedDriverIndex },
    handleShowModal,
    simpleStateUpdater,
  } = useContext(ProviderContext)
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const { updatedDriverDetails, setUpdatedDriverDetails } = useDriverPageContext()

  const [checkEmail, { data: emailCheckData }] = useEmailCheckLazyQuery({
    fetchPolicy: 'no-cache',
  })

  const [email, setEmail] = useState<string>(quote?.details?.proposer?.email || '')
  const [mobileNumber, setMobileNumber] = useState<string>(quote?.details?.proposer?.mobile_number || '')
  const [emailError, setEmailError] = useState<string>('')
  const [mobileError, setMobileError] = useState<string>('')
  const [isMember, setIsMember] = useState<boolean>(false)

  const emailPageError = showErrorBanner && !validInputs[selectedDriverIndex]?.email ? notAnsweredMessage : ''
  const mobilePageError = showErrorBanner && !validInputs[selectedDriverIndex]?.mobileNumber ? notAnsweredMessage : ''

  const checkExistingCustomer = async (email: string) => {
    const lowercaseEmail = email.toLocaleLowerCase()
    if (lowercaseEmail !== 'qa@bymiles.co.uk') {
      checkEmail({ variables: { email: lowercaseEmail } })
    }
  }

  const handleEmailOnBlur = async () => {
    if (!email.match(emailRegex)) {
      const error = email
        ? 'Sorry, this doesn’t look like an email address. Please re-type and try again.'
        : genericErrorCopy
      setEmailError(error)
    } else {
      checkExistingCustomer(email)
    }
    DataLayer.customEvent({
      eventName: 'email',
      section: 'driver',
      eventType: 'form tracking',
    })
  }

  const handleEmailOnChange = async (updatedEmail: string) => {
    simpleStateUpdater([{ state: 'quoteEmail', value: email }])
    setUpdatedDriverDetails({ ...updatedDriverDetails, email: updatedEmail })
  }

  const handleMarketingTogglesOnChange = (updatedMarketing: MarketingProps) => {
    setUpdatedDriverDetails({ ...updatedDriverDetails, marketing: updatedMarketing })
  }

  const handleMobileOnBlur = () => {
    if (!mobileNumber.replaceAll(' ', '').match(mobileRegex)) {
      const error = mobileNumber
        ? 'Sorry, this doesn’t look like a mobile number. Please double check and try again.'
        : genericErrorCopy
      setMobileError(error)
    }
    DataLayer.customEvent({
      eventName: 'mobile number',
      section: 'driver',
      eventType: 'form tracking',
    })
  }

  const handleMobileOnChange = (updatedMobile: string) => {
    setUpdatedDriverDetails({ ...updatedDriverDetails, mobileNumber: updatedMobile.replaceAll(' ', '') })
  }

  useEffect(() => {
    !!emailCheckData?.emailCheck?.isMember ? handleShowModal('existing-member') : setIsMember(false)
  }, [emailCheckData?.emailCheck?.isMember])

  useEffect(() => {
    if (isMember) {
      checkEmail({ variables: { email: '' } })
      setEmail('')
      document.querySelector('.email')?.querySelector('input')?.focus()
    }
  }, [isMember])

  useEffect(() => {
    updateValidInputs({ field: 'email', value: !!email?.match(emailRegex) })
  }, [email])
  useEffect(() => {
    updateValidInputs({ field: 'mobileNumber', value: !!mobileNumber?.replaceAll(' ', '').match(mobileRegex) })
  }, [mobileNumber])

  return (
    <DriverContactDetailsStyle>
      <H4>Your contact details.</H4>
      <GenericSectionStyle>
        <div className="generic-wrapper">
          <GenericContactQuestion
            onBlur={handleEmailOnBlur}
            onChange={(value: string) => handleEmailOnChange(value)}
            setError={setEmailError}
            setValue={setEmail}
            error={emailError || emailPageError}
            value={email}
            showModal={showModal}
            {...emailProps}
          />
          <GenericContactQuestion
            onBlur={handleMobileOnBlur}
            onChange={(value: string) => handleMobileOnChange(value)}
            setError={setMobileError}
            setValue={setMobileNumber}
            error={mobileError || mobilePageError}
            value={mobileNumber}
            showModal={showModal}
            {...mobileProps}
          />
          <div className="question-container" id="marketing">
            <H5>Feel the low mileage love.</H5>
            <ParagraphBody className="question-info">
              Whether you take a policy or not, we’d like to contact you with occasional offers. (You can unsubscribe at
              any time.) Interested?
            </ParagraphBody>
            <MarketingToggles
              section="driver"
              borderColor="aubergine"
              className="driver-marketing-toggles"
              onChange={handleMarketingTogglesOnChange}
            />
            <MessageBanner
              className="security-message banner-message"
              icon="IconLock"
              text={
                <ParagraphBody>
                  <span>
                    {
                      'Your information is safe and secure with us. For more information on how we protect your data, read our '
                    }
                  </span>
                  <span
                    className="privacy-link"
                    onClick={() => window.open('https://www.bymiles.co.uk/privacy-policy', '_blank')?.focus()}
                  >
                    {'privacy notice'}
                  </span>
                  <span>{'.'}</span>
                </ParagraphBody>
              }
            />
          </div>
        </div>
      </GenericSectionStyle>
      {showModal && activeModal === 'existing-member' && <ExistingMemberModal onClose={() => setIsMember(true)} />}
    </DriverContactDetailsStyle>
  )
}
